import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import PlaceRounded from "@mui/icons-material/PlaceRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import GroupRounded from "@mui/icons-material/GroupRounded";
import EventRepeatRounded from "@mui/icons-material/EventRepeatRounded";
import { useAuth } from "@bw/shared/hooks";
import {
  isApiError,
  facilityApi,
  mergeBookingCancellationResponse,
  requiresExactCancellationRetry,
} from "@bw/shared/api";
import {
  formatMoney,
  parseDecimal,
  summarizeBookingSet,
  formatBookingSetLabel,
} from "@bw/shared/utils";
import type {
  BookingInvitationResponse,
  BookingParticipantResponse,
  BookingCancelRequest,
  BookingResponse,
  FacilityResponse,
} from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { DetailHeader, ShareButton } from "../../../common/components/DetailHeader";
import { DenseRow } from "../../../common/components/DenseRow";
import { useBookingDetail } from "../hooks/useBookingDetail";
import { useMyBookings } from "../hooks/useMyBookings";
import { useBookingParticipants } from "../hooks/useBookingParticipants";
import { useBookingInvitations } from "../hooks/useBookingInvitations";
import { useFacility } from "../hooks/useFacility";
import { useCancellationQuote } from "../hooks/useCancellationQuote";
import { useCancelBooking } from "../hooks/useCancelBooking";
import { ParticipantRow } from "../components/ParticipantRow";
import { RemoveParticipantControl } from "../components/RemoveParticipantControl";
import { CancelInvitationControl } from "../components/CancelInvitationControl";
import { InviteParticipantDialog } from "../components/InviteParticipantDialog";
import { BookingPassDialog } from "../components/BookingPassDialog";
import { CancelBookingDialog } from "../components/CancelBookingDialog";
import { ReconfirmPrompt, RequestMoveRow } from "../components/BookingMoveControls";
import { PlayerPeekDialog } from "../../../app/components/PlayerPeekDialog";
import { authorLabel, MISSING, playerHandleSuffix } from "@bw/shared/components";
import { MaterialPlayerCard } from "../../../common/components/MaterialPlayerCard";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Booking-detail hub (slice δ) — the page for ONE court booking, at
 * /book/:facilityId/bookings/:bookingId.
 *
 * THIN presentation layer — every value is server truth rendered verbatim:
 *   - HEADER: the server-resolved COURT name (+ the facility name when available),
 *     a NEUTRAL "Your court" label when blank, NEVER the raw court/booking uuid —
 *     and crucially NOT booking.display_name, which is the BOOKER's name, not the
 *     court. Plus a venue-local human start time and the server status. Honest
 *     role="status" loading, a distinct 404 not-found, and a role="alert" error.
 *   - ROSTER (status-aware): ONE merged list of the booking's participants AND its
 *     pending invitations — each person ONCE, in server order (participants first,
 *     then the Invited rows). The host renders a Host badge (★); an accepted guest
 *     not yet arrived renders "Going"; an arrived guest renders "Checked in" (✓); a
 *     pending invitee not already a participant renders an "Invited" row (the
 *     invitee's resolved display name, em-dash for null — never the raw uid). The
 *     pending-invitations read is HOST-ONLY, so a non-host viewer sees participants
 *     only. No client sort; honest empty/loading/error.
 *   - ADD (host only): the single "Add players" action opens the INVITE flow
 *     (InviteParticipantDialog) — player-search by name → invite → a PENDING
 *     invitation (the invitee joins the roster once they accept). The viewer ===
 *     booking.user_id gate is a UX hint; the backend owns real authz.
 *   - REMOVE/CANCEL (host): a per-guest remove control is absent on the host's
 *     own row and on a checked-in guest; pending Invited rows instead expose the
 *     confirm-gated invitation-cancellation endpoint.
 *   - QR: a "Show my check-in QR" button on a CONFIRMED court, available to ANY
 *     participant → the server-minted pass in a focus-managed dialog. Terminal
 *     courts expose no dead pass control.
 *   - PEEK (B.3): clicking a non-host roster member's name opens PlayerPeekDialog
 *     so the host can view a participant's profile without leaving the booking hub.
 */
export function BookingDetailPage() {
  const { facilityId, bookingId } = useParams<{
    facilityId: string;
    bookingId: string;
  }>();
  const { user } = useAuth();
  const viewerUid = user?.uid;

  const bookingQ = useBookingDetail(bookingId);
  const rosterQ = useBookingParticipants(facilityId, bookingId);
  // The booking response carries its server-resolved court label. The facility
  // supplies secondary venue context; a missing name just degrades the title.
  const facilityQ = useFacility(facilityId);
  // Host-only: this booking's OUTGOING pending invitations. Read so the merged
  // roster can show an "Invited" row per pending invitee AND so the invite dialog
  // can EXCLUDE already-pending invitees (re-inviting would 409). Gated on the
  // viewer being the host (only the host can list/create invites); a non-host
  // viewer never fires this read. `booking.user_id` is read from the (possibly not
  // yet resolved) detail query — on the render where it resolves to the viewer, the
  // gate flips on and the read fires.
  const viewerIsHost =
    Boolean(viewerUid) && bookingQ.data?.user_id === viewerUid;
  const invitationsQ = useBookingInvitations(
    facilityId,
    bookingId,
    viewerIsHost,
  );

  // Set-aware detail: if the anchor booking belongs to a recurring group (a
  // multi-court / recurring reservation — every court_booking of a batch checkout
  // shares one recurring_group_id), load its SIBLINGS from the per-facility list
  // (REUSED — no new endpoint) so the page can render the WHOLE reservation. Gated
  // on a known group id so a SINGLE booking fires no extra read and its view stays
  // byte-for-byte unchanged. The list includes accepted participants, so a guest
  // can re-enter the whole set after the pending invitation leaves their inbox.
  const groupId = bookingQ.data?.recurring_group_id ?? null;
  const siblingsQ = useMyBookings(groupId ? facilityId : undefined);

  // Which overlay is open. Only one at a time. The single host "Add players"
  // action opens the INVITE dialog; the QR action opens the pass dialog.
  const [showAdd, setShowAdd] = useState(false);
  const [showPass, setShowPass] = useState(false);
  // B.3 — roster peek state. Tracks which non-host participant's profile is open.
  const [peek, setPeek] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);

  // ---- Booking-level states (header) ----------------------------------------
  if (bookingQ.isLoading) {
    return (
      <Page>
        <LoadingState label="Loading this booking…" />
      </Page>
    );
  }

  if (bookingQ.isError) {
    // A 404 is its OWN honest "this booking is gone" state — not a generic error.
    if (isApiError(bookingQ.error) && bookingQ.error.status === 404) {
      return (
        <Page>
          <Alert severity="error" role="alert">
            We couldn&apos;t find this booking. It may no longer exist.
          </Alert>
        </Page>
      );
    }
    return (
      <Page>
        <Alert severity="error" role="alert">
          Something went wrong loading this booking. Please try again.
        </Alert>
      </Page>
    );
  }

  const booking = bookingQ.data;
  if (!booking) {
    return (
      <Page>
        <Alert severity="error" role="alert">
          We couldn&apos;t find this booking. It may no longer exist.
        </Alert>
      </Page>
    );
  }

  // The header leads with WHERE (court / facility), never WHO. booking.display_name
  // is the BOOKER's name — not the court — so it must NOT be the title. Resolve the
  // server-authored court name; fall back to a NEUTRAL "Your court" when blank,
  // NEVER the raw court_id / booking_id uuid. B.6: the facility name becomes a
  // <Link> to /facility/{facilityId} when resolved; guarded so a pending facilityQ
  // never yields a broken href="/facility/undefined".
  const courtName = booking.court_name?.trim() || "Your court";
  // The booking carries its venue name; the facility read adds address/zone.
  const facilityName = facilityQ.data?.name?.trim() || booking.facility_name?.trim();
  const isHost = Boolean(viewerUid) && booking.user_id === viewerUid;
  const bookingEndMs = Date.parse(booking.end_time);
  const canInvite =
    isHost &&
    booking.status === "confirmed" &&
    booking.cancellation_recovery_pending !== true &&
    !Number.isNaN(bookingEndMs) &&
    bookingEndMs > Date.now();
  const canCheckIn =
    booking.status === "confirmed" &&
    booking.cancellation_recovery_pending !== true;

  // The other courts of this reservation (same recurring_group_id). Rendered ONLY
  // when there is genuinely more than one (a real multi-booking set); otherwise
  // the single-court view below renders exactly as today.
  const siblings = groupId
    ? (siblingsQ.data ?? []).filter((b) => b.recurring_group_id === groupId)
    : [];
  // A known group must never silently collapse to its anchor while the complete
  // list is uncertain. This includes a cached empty value during a background
  // refetch: wait for canonical siblings rather than flashing a fake singleton.
  if (groupId && siblings.length <= 1 && siblingsQ.isFetching) {
    return (
      <Page>
        <LoadingState label="Loading this reservation…" />
      </Page>
    );
  }
  if (
    groupId &&
    siblings.length <= 1 &&
    (siblingsQ.isError || siblings.length === 0)
  ) {
    return (
      <Page>
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              type="button"
              color="inherit"
              size="small"
              onClick={() => void siblingsQ.refetch()}
            >
              Try again
            </Button>
          }
        >
          We couldn&apos;t load every court in this reservation. Try again to
          keep all check-in passes reachable.
        </Alert>
      </Page>
    );
  }
  if (groupId && siblings.length > 1) {
    return (
      <ReservationDetail
        facilityId={facilityId as string}
        anchor={booking}
        siblings={siblings}
        facilityName={facilityName}
        isHost={isHost}
        refetchBookings={siblingsQ.refetch}
      />
    );
  }

  const statusText =
    booking.cancellation_recovery_pending === true
      ? "Cancellation processing"
      : statusLabel(booking.status);
  const timeZone = facilityQ.data?.timezone;
  const pass = passLabels(booking.start_time, booking.end_time, timeZone);
  const participants = rosterQ.data ?? [];
  const participantIds = new Set(participants.map((p) => p.user_id));
  const pendingInvites = (invitationsQ.data ?? []).filter(
    (inv) => inv.status === "pending" && !participantIds.has(inv.invitee_id),
  );
  // The roster list shows whenever there is more than the booker: other
  // players, or (host only) pending invitations — including while the host's
  // invitations are still loading, so invited rows never flash in and out.
  const showRoster =
    participants.length > 1 ||
    pendingInvites.length > 0 ||
    (isHost && invitationsQ.isLoading && participants.length > 0);
  const playersSummary = rosterQ.isLoading
    ? "Loading players…"
    : rosterQ.isError
      ? "Players unavailable right now"
      : showRoster
        ? `${participants.length} ${participants.length === 1 ? "player" : "players"}${
            pendingInvites.length ? ` · ${pendingInvites.length} invited` : ""
          }`
        : participants.length === 1
          ? playerIdentity(participants[0])
          : "No players listed yet";
  const directions = facilityQ.data ? facilityDirections(facilityQ.data) : null;
  // #2691: only the owner may request a move of a confirmed, future booking.
  const canRequestMove =
    isHost &&
    booking.status === "confirmed" &&
    booking.cancellation_recovery_pending !== true &&
    Date.parse(booking.start_time) > Date.now();
  const paid =
    booking.amount_charged != null
      ? formatMoney(booking.amount_charged, booking.currency)
      : null;

  return (
    <Page narrow>
      <Stack spacing={2}>
        <DetailHeader
          back={{ to: "/me/bookings", label: "Back to bookings" }}
          title="Booking"
          actions={<ShareButton title="Booking" />}
          titleComponent="p"
          subtitle={canCheckIn ? statusText : undefined}
        />

        {/* ---- The pass: confirmation doubles as the check-in pass. ---- */}
        <Paper
          component="header"
          variant="outlined"
          sx={{ borderRadius: "12px", px: 2, py: 2, textAlign: "center" }}
        >
          <Stack spacing={1.25} sx={{ alignItems: "center" }}>
            {canCheckIn ? (
              <Typography
                sx={{ fontSize: "0.8125rem", fontWeight: 800, color: "primary.main", letterSpacing: 0.3 }}
              >
                CONFIRMED · CHECK-IN PASS
              </Typography>
            ) : (
              <Chip
                size="small"
                variant="outlined"
                color={statusColor(booking.status)}
                label={statusText}
                data-booking-status={booking.status}
              />
            )}
            {canCheckIn ? (
              <ButtonBase
                type="button"
                aria-label={`Show my check-in QR for ${courtName}`}
                onClick={() => setShowPass(true)}
                sx={{
                  width: 150,
                  height: 150,
                  borderRadius: "12px",
                  bgcolor: "common.white",
                  color: "common.black",
                  display: "grid",
                  placeItems: "center",
                  "&.Mui-focusVisible": { outline: 2, outlineColor: "primary.main", outlineOffset: 3 },
                }}
              >
                <Stack spacing={0.5} sx={{ alignItems: "center" }}>
                  <QrCode2Rounded aria-hidden sx={{ fontSize: 88 }} />
                  <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 700 }}>
                    Show my check-in QR
                  </Typography>
                </Stack>
              </ButtonBase>
            ) : null}
            {/* The pass leads with WHERE and WHEN (court · date), never WHO. */}
            <Typography
              component="h1"
              sx={{ fontSize: "1.125rem", fontWeight: 700, overflowWrap: "anywhere" }}
            >
              {courtName}
              {pass.day ? ` · ${pass.day}` : null}
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: "-4px !important", overflowWrap: "anywhere" }}>
              {pass.time}
              {facilityName ? (
                <>
                  {pass.time ? " · " : null}
                  <Link
                    component={RouterLink}
                    to={`/facility/${facilityId}`}
                    color="inherit"
                    sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                  >
                    {facilityName}
                  </Link>
                </>
              ) : null}
            </Typography>
          </Stack>
        </Paper>

        {viewerUid && booking.status === "confirmed" ? (
          <ReconfirmPrompt bookingId={booking.booking_id} startTime={booking.start_time} />
        ) : null}

        {/* ---- Players, receipt and directions, one tap away ---- */}
        <List disablePadding>
          <DenseRow
            leading={<RowTile icon={<GroupRounded fontSize="small" />} />}
            title="Players"
            subtitle={playersSummary}
          />
            {paid ? (
              <DenseRow
                to={booking.transaction_id ? `/me/transactions/${booking.transaction_id}` : undefined}
                ariaLabel={booking.transaction_id ? `Receipt, ${paid}` : undefined}
                leading={<RowTile icon={<ReceiptLongRounded fontSize="small" />} />}
                title="Receipt"
                subtitle={`${paid} paid`}
                trailing={booking.transaction_id ? <ChevronRightRounded color="action" aria-hidden /> : undefined}
              />
            ) : null}
            {directions ? (
              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href={directions.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ gap: 1.5, px: { xs: 0.5, sm: 1 }, py: 0.75, borderRadius: "8px" }}
                >
                  <RowTile icon={<PlaceRounded fontSize="small" />} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>Directions</Typography>
                    <Typography noWrap variant="body2" color="text.secondary">
                      {directions.line}
                    </Typography>
                  </Box>
                  <ChevronRightRounded color="action" aria-hidden />
                </ListItemButton>
              </ListItem>
            ) : null}
            {canRequestMove && timeZone ? (
              <RequestMoveRow
                booking={booking}
                tile={<RowTile icon={<EventRepeatRounded fontSize="small" />} />}
                timezone={timeZone}
              />
            ) : null}
        </List>

        {/* "Add players" opens the INVITE flow (host only, confirmed + future). */}
        {canInvite ? (
          <Button
            type="button"
            variant="outlined"
            onClick={() => setShowAdd(true)}
            sx={{ alignSelf: "stretch", borderColor: "divider", color: "text.primary" }}
          >
            Add players
          </Button>
        ) : null}

        {/* ---- Roster ----
            Hidden while the Add (invite) dialog is open: it is a focused modal over
            the roster, and the picker surfaces the SAME people, so showing both at
            once would render an identity twice. On close the roster + invitations
            refetch and show the server's updated truth. */}
        <CancellationRecoveryControl booking={booking} />
        {/* Who is on the court, with the host's controls — only when there is
            more than the booker to show (the Players row above summarises). */}
        {showAdd || !(showRoster || rosterQ.isError) ? null : (
          <Box component="section" aria-label="Players">
            <RosterBody
              facilityId={facilityId as string}
              bookingId={bookingId as string}
              isHost={isHost}
              isLoading={rosterQ.isLoading}
              isError={rosterQ.isError}
              participants={rosterQ.data}
              invitations={invitationsQ.data}
              onPeek={(userId, displayName) => setPeek({ userId, displayName })}
            />
          </Box>
        )}
      </Stack>

      {showAdd ? (
        <InviteParticipantDialog
          facilityId={facilityId as string}
          bookingId={bookingId as string}
          participants={rosterQ.data ?? []}
          pendingInviteeIds={(invitationsQ.data ?? []).map(
            (inv) => inv.invitee_id,
          )}
          onClose={() => setShowAdd(false)}
        />
      ) : null}

      {showPass ? (
        <BookingPassDialog
          bookingId={bookingId as string}
          onClose={() => setShowPass(false)}
        />
      ) : null}

      {/* B.3 — roster peek dialog. Rendered outside the roster section so it
          overlays the full page without being clipped by the roster container. */}
      {peek ? (
        <PlayerPeekDialog
          userId={peek.userId}
          displayName={peek.displayName}
          onClose={() => setPeek(null)}
        />
      ) : null}
    </Page>
  );
}

/**
 * The roster section body: honest loading / error / empty, else ONE status-aware
 * list merging the booking's participants with its pending invitations.
 *
 *   - Participants render in SERVER order (no client re-sort) FIRST: the host with
 *     a Host badge (★), an accepted-but-not-arrived guest as "Going", an arrived
 *     guest as "Checked in" — all via ParticipantRow.
 *   - Then each PENDING invitation whose invitee is NOT already a participant
 *     renders an "Invited" row (the invitee's resolved display name, em-dash for
 *     null — never the raw uid). Invited rows are HOST-ONLY (a non-host viewer
 *     can't list invitations, so `invitations` is undefined for them) and expose
 *     the confirm-gated Cancel invite action instead of participant removal.
 *
 * Each person appears ONCE: a pending invite for someone already a participant is
 * dropped (the participant row is the source of truth). The host gets a per-guest
 * Remove on accepted guests (never on their own row, never on a checked-in guest).
 *
 * `onPeek` is threaded through to each non-host ParticipantRow so the page's
 * peek state can be set from the row without the row owning dialog state.
 */
function CancellationRecoveryControl({
  booking,
}: {
  booking: BookingResponse;
}) {
  const client = useQueryClient();
  const recovery = useMutation({
    mutationFn: () =>
      facilityApi.recoverBookingCancellation(booking.booking_id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["booking"] });
      void client.invalidateQueries({ queryKey: queryKeys.play.myUpcoming() });
    },
  });
  if (
    !booking.cancellation_recovery_pending ||
    !booking.cancellation_recovery_allowed
  ) {
    return null;
  }
  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      <Button
        size="small"
        variant="outlined"
        disabled={recovery.isPending}
        onClick={() => recovery.mutate()}
      >
        {recovery.isPending ? "Retrying cancellation…" : "Retry cancellation"}
      </Button>
      {recovery.isError ? (
        <Alert severity="error">
          We couldn&apos;t finish the cancellation. Your booking is still
          processing. Try again to resume the same cancellation.
        </Alert>
      ) : null}
    </Stack>
  );
}

function RosterBody({
  facilityId,
  bookingId,
  isHost,
  isLoading,
  isError,
  participants,
  invitations,
  onPeek,
}: {
  facilityId: string;
  bookingId: string;
  isHost: boolean;
  isLoading: boolean;
  isError: boolean;
  participants: BookingParticipantResponse[] | undefined;
  // The booking's OUTGOING invitations (host-only). Undefined for a non-host
  // viewer (the read never fires) → no Invited rows for them.
  invitations: BookingInvitationResponse[] | undefined;
  // B.3: page-level peek callback. Passed to non-host rows only.
  onPeek: (userId: string, displayName: string) => void;
}) {
  if (isLoading) {
    return <LoadingState label="Loading players…" compact />;
  }
  if (isError) {
    return (
      <Alert severity="error" role="alert" sx={{ m: 2 }}>
        We couldn&apos;t load the players for this booking. Please try again.
      </Alert>
    );
  }
  const list = participants ?? [];

  // PENDING invitations whose invitee is NOT already a participant — each becomes
  // an "Invited" row. A stale pending invite for someone already on the roster is
  // dropped so a person appears ONCE (the participant row wins). Server order is
  // honored (no client re-sort). Host-only: `invitations` is undefined for a
  // non-host viewer.
  const participantIds = new Set(list.map((p) => p.user_id));
  const invited = (invitations ?? []).filter(
    (inv) => inv.status === "pending" && !participantIds.has(inv.invitee_id),
  );

  if (list.length === 0 && invited.length === 0) {
    return (
      <Typography role="status" variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 1 }, py: 1 }}>
        No players on this booking yet.
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {/* Participants in SERVER order — rendered verbatim, no client re-sort. */}
      {list.map((p) => {
        const checkedIn = Boolean(p.checked_in_at);
        const isOwnHostRow = p.role === "host";
        // Remove is host-only, never on the host's own row, never on a
        // checked-in guest (the server would 409 — no dead control).
        const canRemove = isHost && !isOwnHostRow && !checkedIn;
        return (
          <ParticipantRow
            key={p.user_id}
            participant={p}
            removeSlot={
              canRemove ? (
                <RemoveParticipantControl
                  facilityId={facilityId}
                  bookingId={bookingId}
                  participant={p}
                />
              ) : undefined
            }
            // Every resolvable identity gets the same profile affordance. The
            // server profile projection remains authoritative for privacy.
            onPeek={onPeek}
          />
        );
      })}
      {/* Then the pending invitees (host-only) — "Invited" rows with Cancel for host. */}
      {invited.map((inv) => (
        <InvitedRow
          key={inv.invitation_id}
          facilityId={facilityId}
          bookingId={bookingId}
          invitation={inv}
          isHost={isHost}
          onPeek={onPeek}
        />
      ))}
    </List>
  );
}

/**
 * One pending-invitation row in the merged roster. Renders the invitee via
 * <PlayerCard> (initials avatar + name + #handle discriminator + peek button),
 * with "Invited" chip in the card's actions slot. The host additionally gets a
 * "Cancel invite" confirm-gated control alongside the chip (CancelInvitationControl).
 * Avatar and rank are absent from the invitation payload — PlayerCard degrades
 * cleanly to an initials chip + "Unrated".
 *
 * PlayerCard never renders the raw invitee_id (no-UID-in-UI rule): the #last4
 * handle from playerHandleSuffix is the only identity discriminator shown.
 */
function InvitedRow({
  facilityId,
  bookingId,
  invitation,
  isHost,
  onPeek,
}: {
  facilityId: string;
  bookingId: string;
  invitation: BookingInvitationResponse;
  isHost: boolean;
  onPeek: (userId: string, displayName: string) => void;
}) {
  // Em-dash for an unresolved (null) display name — NEVER the raw invitee_id
  // (no-UID-in-UI rule). A single PlayerCard path handles both cases: its
  // #handle discriminator stays useful even when the name is missing, and its
  // initials chip degrades to "?" for the em-dash (initials() drops non-letter
  // leading chars). PlayerCard never renders the raw invitee_id as visible text.
  const name = invitation.invitee_display_name?.trim() || "—";
  return (
    <ListItem divider>
      <MaterialPlayerCard
        userId={invitation.invitee_id}
        displayName={name}
        onClickIdentity={() => onPeek(invitation.invitee_id, name)}
        actions={
          <>
            <Chip label="Invited" size="small" variant="outlined" />
            {isHost ? (
              <CancelInvitationControl
                facilityId={facilityId}
                bookingId={bookingId}
                invitation={invitation}
              />
            ) : null}
          </>
        }
      />
    </ListItem>
  );
}

/** Venue-local "Tue, Jun 20 · 5:00 PM" for a booking start, best-effort. Never
 *  leaks the raw ISO string or "Invalid Date". */
function whenLabel(startTime: string): string {
  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(startTime));
    if (/invalid/i.test(formatted)) return "Time unavailable";
    return formatted;
  } catch {
    return "Time unavailable";
  }
}

function LoadingState({
  label,
  compact = false,
}: {
  label: string;
  compact?: boolean;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      role="status"
      aria-live="polite"
      sx={{
        py: compact ? 2 : 4,
        px: compact ? 2 : 0,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={22} />
      <Typography color="text.secondary">{label}</Typography>
    </Stack>
  );
}

/** The pass's leading tile for a receipt / directions row. */
function RowTile({ icon }: { icon: React.ReactNode }) {
  return (
    <Box
      aria-hidden
      sx={{ width: 44, height: 44, borderRadius: "8px", bgcolor: "action.hover", display: "grid", placeItems: "center", color: "text.secondary", flexShrink: 0 }}
    >
      {icon}
    </Box>
  );
}

/** The venue's address line and a map link for it; null without an address. */
function facilityDirections(
  facility: FacilityResponse,
): { line: string; href: string } | null {
  const address = facility.address;
  const cityState = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const line = (address ? [address.line1, cityState || null].filter(Boolean).join(", ") : "") || facility.location;
  if (!line?.trim()) return null;
  return {
    line,
    href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${facility.name}, ${line}`)}`,
  };
}

/** "Sat, Oct 31" and "7:00–8:00 PM" in the venue's zone when known; never raw ISO. */
function passLabels(
  startTime: string,
  endTime: string,
  timeZone?: string,
): { day: string | null; time: string } {
  try {
    const zone = timeZone ? { timeZone } : {};
    const start = new Date(startTime);
    const end = new Date(endTime);
    const day = new Intl.DateTimeFormat("en-US", { ...zone, weekday: "short", month: "short", day: "numeric" }).format(start);
    const time = new Intl.DateTimeFormat("en-US", { ...zone, hour: "numeric", minute: "2-digit" });
    if (/invalid/i.test(day)) return { day: null, time: "Time unavailable" };
    if (Number.isNaN(end.getTime()) || end <= start) return { day, time: time.format(start) };
    return { day, time: time.formatRange(start, end) };
  } catch {
    return { day: null, time: whenLabel(startTime) };
  }
}

/** "Name #ab12" for a roster entry; the em-dash placeholder never carries a handle. */
function playerIdentity(participant: BookingParticipantResponse): React.ReactNode {
  const name = authorLabel(participant.display_name, participant.user_id);
  if (name === MISSING) return name;
  return (
    <>
      <span>{name}</span> <span>{playerHandleSuffix(participant.user_id)}</span>
    </>
  );
}

/** Human-readable booking status (server value, title-cased). */
function statusLabel(status: string): string {
  switch (status) {
    case "confirmed":
      return "Confirmed";
    case "cancelled":
      return "Cancelled";
    case "completed":
      return "Completed";
    case "no_show":
    case "no-show":
      return "No-show";
    case "pending":
      return "Pending";
    default:
      return status;
  }
}

function statusColor(
  status: string,
): "default" | "success" | "error" | "warning" {
  switch (status) {
    case "confirmed":
      return "success";
    case "cancelled":
    case "no_show":
    case "no-show":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
}

/**
 * The SET-AWARE reservation view — rendered when the anchor booking belongs to a
 * recurring group with more than one court. Presents the reservation as ONE unit,
 * matching the confirmation screen + the "invite the whole set" behaviour:
 *
 *   - ONE reservation header: the facility (a <Link>), the adaptive set label
 *     (e.g. "3 courts · Sat, Jun 20 · 5:00 – 6:00 PM"), and the SINGLE reservation
 *     total (Σ amount_charged — real charged amounts, no client pricing).
 *   - ONE set-level "Add players" (host only) — it already fans the invite to the
 *     WHOLE group server-side (posts the anchor booking id), so it is NOT per-court.
 *   - EACH court as its own sub-section (CourtSection): the court's time + status,
 *     its roster + check-in QR, and a host-only PER-COURT Cancel backed by a
 *     server-issued quote; there is NO whole-set cancel.
 *
 * All cancel + peek dialog state is owned here (one dialog at a time), mirroring
 * the cross-facility calendar's cancel flow.
 */
function ReservationDetail({
  facilityId,
  anchor,
  siblings,
  facilityName,
  isHost,
  refetchBookings,
}: {
  facilityId: string;
  anchor: BookingResponse;
  siblings: BookingResponse[];
  facilityName: string | undefined;
  isHost: boolean;
  refetchBookings: () => Promise<{
    data?: BookingResponse[];
    isError: boolean;
  }>;
}) {
  // Order the courts by start_time (NaN-safe) for a stable reading order.
  const orderedRows = useMemo(() => {
    const ms = (b: BookingResponse) => {
      const t = Date.parse(b.start_time);
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    return [...siblings].sort((a, b) => ms(a) - ms(b));
  }, [siblings]);

  const summary = useMemo(
    () => summarizeBookingSet(orderedRows),
    [orderedRows],
  );
  const label = formatBookingSetLabel(summary);
  // Participant list responses redact every host-owned amount. Unknown is not
  // free: preserve the redaction instead of folding null rows into a $0 total.
  const hasKnownTotal = orderedRows.every(
    (booking) => booking.amount_charged != null,
  );
  const total = hasKnownTotal ? summary.totalCents / 100 : null;

  // The anchor booking's roster + pending invitations. The set-level "Add players"
  // posts the ANCHOR booking id (the batch invite fans to the whole group), so the
  // picker must pre-exclude who is already on the anchor's roster / already invited
  // — a re-invite only earns a graceful 409. These reads share query keys with the
  // anchor CourtSection's own reads (React Query dedupes), so they're not extra
  // network. Gated to the host: a non-host can't list invitations.
  const anchorRosterQ = useBookingParticipants(facilityId, anchor.booking_id);
  const anchorInvitationsQ = useBookingInvitations(
    facilityId,
    anchor.booking_id,
    isHost,
  );

  const [cancelTarget, setCancelTarget] = useState<BookingResponse | null>(
    null,
  );
  const [quoteRequestVersion, setQuoteRequestVersion] = useState(0);
  const [decisionChanged, setDecisionChanged] = useState(false);
  const [cancellationNotice, setCancellationNotice] = useState("");
  const cancellationNoticeRef = useRef<HTMLParagraphElement>(null);
  const cancellationRequestRef = useRef<BookingCancelRequest | null>(null);
  const [cancelled, setCancelled] = useState<Record<string, BookingResponse>>(
    {},
  );
  const [showAdd, setShowAdd] = useState(false);
  const [peek, setPeek] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);

  const cancel = useCancelBooking(facilityId);
  const quoteQ = useCancellationQuote(
    cancelTarget?.booking_id,
    quoteRequestVersion,
    Boolean(cancelTarget),
  );

  const rows = useMemo(
    () => orderedRows.map((b) => cancelled[b.booking_id] ?? b),
    [cancelled, orderedRows],
  );
  const canInviteToAnyCourt = rows.some((booking) => {
    const endMs = Date.parse(booking.end_time);
    return (
      booking.status === "confirmed" &&
      booking.cancellation_recovery_pending !== true &&
      !Number.isNaN(endMs) &&
      endMs > Date.now()
    );
  });

  useEffect(() => {
    if (!cancelTarget) return;
    const canonical = rows.find(
      (booking) => booking.booking_id === cancelTarget.booking_id,
    );
    if (
      canonical?.status === "confirmed" &&
      canonical.cancellation_recovery_pending !== true
    ) {
      setCancelTarget(canonical);
      return;
    }
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    cancel.reset();
    setCancelTarget(null);
    setCancellationNotice(
      canonical
        ? "The booking status changed. Cancellation controls closed."
        : "The booking is no longer available. Cancellation controls closed.",
    );
  }, [cancel, cancelTarget?.booking_id, rows]);

  useEffect(() => {
    if (cancellationNotice) cancellationNoticeRef.current?.focus();
  }, [cancellationNotice]);

  function courtNameFor(b: BookingResponse): string {
    return b.court_name?.trim() || "Your court";
  }
  function handleConfirmCancel() {
    if (!cancelTarget || !quoteQ.data) return;
    const request = cancellationRequestRef.current ?? {
      quote_id: quoteQ.data.quote_id,
      idempotency_key: crypto.randomUUID(),
    };
    cancellationRequestRef.current = request;
    setDecisionChanged(false);
    cancel.mutate(
      {
        bookingId: cancelTarget.booking_id,
        request,
      },
      {
        onSuccess: (res) => {
          setCancelled((prev) => ({
            ...prev,
            [res.booking_id]: mergeBookingCancellationResponse(
              cancelTarget,
              res,
            ),
          }));
          setCancelTarget(null);
        },
        onError: async (error) => {
          if (isApiError(error) && error.details?.refresh_required === true) {
            cancellationRequestRef.current = null;
            const refreshed = await refetchBookings();
            if (refreshed.isError) {
              setCancelTarget(null);
              setCancellationNotice(
                "The current booking status could not be confirmed. Cancellation controls closed.",
              );
              return;
            }
            const canonical = refreshed.data?.find(
              (booking) => booking.booking_id === cancelTarget.booking_id,
            );
            if (
              canonical?.status === "confirmed" &&
              canonical.cancellation_recovery_pending !== true
            ) {
              setCancelTarget(canonical);
              setDecisionChanged(true);
              setQuoteRequestVersion((version) => version + 1);
            } else {
              cancel.reset();
              setDecisionChanged(false);
              setCancelTarget(null);
              setCancellationNotice(
                canonical
                  ? "The booking status changed. Cancellation controls closed."
                  : "The booking is no longer available. Cancellation controls closed.",
              );
            }
          }
        },
      },
    );
  }
  function openCancel(b: BookingResponse) {
    if (!isHost) return;
    cancel.reset();
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    setCancellationNotice("");
    setQuoteRequestVersion((version) => version + 1);
    setCancelTarget(b);
  }

  function closeCancel() {
    if (requiresExactCancellationRetry(cancel.error)) return;
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    setCancelTarget(null);
  }

  function refreshCancellationQuote() {
    if (requiresExactCancellationRetry(cancel.error)) return;
    cancellationRequestRef.current = null;
    cancel.reset();
    setDecisionChanged(false);
    setQuoteRequestVersion((version) => version + 1);
  }

  return (
    <Page>
      {cancellationNotice ? (
        <Box
          component="p"
          ref={cancellationNoticeRef}
          role="status"
          aria-live="polite"
          tabIndex={-1}
          sx={{ m: 0, mb: 2 }}
        >
          {cancellationNotice}
        </Box>
      ) : null}
      <Stack spacing={2.5}>
        {/* ---- Reservation header ---- */}
        <Box component="header">
          <Typography component="h1" variant="h4">
            {facilityName ? (
              <Link component={RouterLink} to={`/facility/${facilityId}`}>
                {facilityName}
              </Link>
            ) : (
              "Your reservation"
            )}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            <Box component="span">{label}</Box>
            <Box component="span" aria-hidden="true" sx={{ mx: 0.75 }}>
              ·
            </Box>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {formatMoney(total, summary.currency)} total
            </Box>
          </Typography>
        </Box>

        {/* ---- Set-level "Add players" (host only) — fans to the whole group ---- */}
        {isHost && canInviteToAnyCourt ? (
          <Stack direction="row">
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setShowAdd(true)}
            >
              Add players
            </Button>
          </Stack>
        ) : null}

        {/* ---- One sub-section per court ---- */}
        <Stack spacing={2}>
          {rows.map((b) => (
            <CourtSection
              key={b.booking_id}
              facilityId={facilityId}
              booking={b}
              courtName={courtNameFor(b)}
              isHost={isHost}
              onCancel={openCancel}
              onPeek={(userId, displayName) => setPeek({ userId, displayName })}
            />
          ))}
        </Stack>
      </Stack>

      {showAdd ? (
        <InviteParticipantDialog
          facilityId={facilityId}
          // The ANCHOR booking id — the batch invite endpoint fans the invite to
          // the whole recurring group, so this is the single set-level invite.
          bookingId={anchor.booking_id}
          // Pre-exclude the anchor's existing roster + pending invitees so the
          // picker matches the single-court path (no re-invite that only 409s).
          participants={anchorRosterQ.data ?? []}
          pendingInviteeIds={(anchorInvitationsQ.data ?? []).map(
            (inv) => inv.invitee_id,
          )}
          onClose={() => setShowAdd(false)}
        />
      ) : null}

      {cancelTarget ? (
        <CancelBookingDialog
          booking={cancelTarget}
          quote={quoteQ.data}
          quoteLoading={quoteQ.isFetching}
          quoteError={quoteQ.error}
          onQuoteRetry={refreshCancellationQuote}
          onConfirm={handleConfirmCancel}
          onClose={closeCancel}
          cancelPending={cancel.isPending}
          cancelError={cancel.error}
          decisionChanged={decisionChanged}
        />
      ) : null}

      {peek ? (
        <PlayerPeekDialog
          userId={peek.userId}
          displayName={peek.displayName}
          onClose={() => setPeek(null)}
        />
      ) : null}
    </Page>
  );
}

/**
 * One court's sub-section inside the reservation view: the court name + its
 * venue-local time + status, a "Show my check-in QR" for any participant only
 * while that court is confirmed, a per-court Cancel (only while confirmed +
 * future — the server would reject a terminal/past cancel), and the court's own
 * roster (participants + pending invitations, merged). Each section owns its own
 * roster/invitations reads + QR-pass dialog; cancel is delegated up to the
 * reservation via `onCancel`.
 */
function CourtSection({
  facilityId,
  booking,
  courtName,
  isHost,
  onCancel,
  onPeek,
}: {
  facilityId: string;
  booking: BookingResponse;
  courtName: string;
  isHost: boolean;
  onCancel: (booking: BookingResponse) => void;
  onPeek: (userId: string, displayName: string) => void;
}) {
  const rosterQ = useBookingParticipants(facilityId, booking.booking_id);
  const invitationsQ = useBookingInvitations(
    facilityId,
    booking.booking_id,
    isHost,
  );
  const [showPass, setShowPass] = useState(false);

  // Cancellable only while confirmed AND future — the same honest gate the
  // booking rows use (a terminal/past booking is rejected server-side).
  const startMs = Date.parse(booking.start_time);
  const isFuture = !Number.isNaN(startMs) && startMs > Date.now();
  const isCancellable =
    isHost &&
    booking.status === "confirmed" &&
    booking.cancellation_recovery_pending !== true &&
    isFuture;
  const canCheckIn =
    booking.status === "confirmed" &&
    booking.cancellation_recovery_pending !== true;

  return (
    <Paper component="section" variant="outlined" sx={{ overflow: "hidden" }}>
      <Stack component="header" spacing={1} sx={{ p: 2, pb: 1.5 }}>
        <Typography component="h2" variant="h6">
          {courtName}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <Typography color="text.secondary" variant="body2">
            {whenLabel(booking.start_time)}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            color={statusColor(booking.status)}
            label={
              booking.cancellation_recovery_pending === true
                ? "Cancellation processing"
                : statusLabel(booking.status)
            }
            data-booking-status={booking.status}
          />
          {/* A cancelled court keeps its per-court refund visible (parity with the
              flat CalendarBookingRow's "Cancelled · $X refunded"). Server field
              only — no client refund math; hidden when the server sent no amount. */}
          {booking.status === "cancelled" &&
          parseDecimal(booking.refund_amount) !== null ? (
            <Typography
              color="success.main"
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              {formatMoney(booking.refund_amount, booking.currency ?? "USD")}{" "}
              refunded
            </Typography>
          ) : null}
        </Stack>
      </Stack>

      {canCheckIn || isCancellable ? (
        <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1.5 }}>
          {canCheckIn ? (
            <Button
              type="button"
              variant="contained"
              size="small"
              aria-label={`Show my check-in QR for ${courtName} at ${whenLabel(booking.start_time)}`}
              onClick={() => setShowPass(true)}
            >
              Show my check-in QR
            </Button>
          ) : null}
          {isCancellable ? (
            <Button
              type="button"
              variant="text"
              color="error"
              size="small"
              onClick={() => onCancel(booking)}
            >
              Cancel
            </Button>
          ) : null}
        </Stack>
      ) : null}

      <Box sx={{ px: 2 }}>
        <CancellationRecoveryControl booking={booking} />
      </Box>
      <RosterBody
        facilityId={facilityId}
        bookingId={booking.booking_id}
        isHost={isHost}
        isLoading={rosterQ.isLoading}
        isError={rosterQ.isError}
        participants={rosterQ.data}
        invitations={invitationsQ.data}
        onPeek={onPeek}
      />

      {showPass ? (
        <BookingPassDialog
          bookingId={booking.booking_id}
          onClose={() => setShowPass(false)}
        />
      ) : null}
    </Paper>
  );
}
